import React from 'react';
import { Loader2 } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import type { ImportedAllergyLineResponse } from '../../services/api';
import {
  NKA_CODED_ALLERGIES_BLOCK_COPY,
  NKA_DISMISS_IMPORTED_CONFIRM_LABEL,
  NKA_DISMISS_IMPORTED_TITLE,
  NKA_DISMISS_IMPORTED_WARNING,
} from './importedChartLineCopy';

/**
 * The gate in front of asserting No Known Allergies (2026-08-28 ruling).
 *
 * Two states, two very different answers. Imported Kinnser lines awaiting
 * review contradict the assertion, so the clinician settles both in one
 * deliberate act — the modal lists exactly what gets dismissed, and the
 * confirm verb says so. Coded allergies are clinician-entered records;
 * removing them is its own deliberate act, so that state only explains and
 * offers no shortcut.
 */
export type NkaGuardState = 'closed' | 'dismiss-imported' | 'coded-blocked';

const NkaGuardDialog: React.FC<{
  state: NkaGuardState;
  /** Outstanding imported allergy lines the assertion would dismiss. */
  lines: ImportedAllergyLineResponse[];
  /** The dismiss-and-assert flow is running; both buttons hold. */
  isWorking: boolean;
  onConfirmDismiss: () => void;
  onClose: () => void;
}> = ({ state, lines, isWorking, onConfirmDismiss, onClose }) => (
  <Dialog
    open={state !== 'closed'}
    onOpenChange={open => {
      if (!open && !isWorking) onClose();
    }}
  >
    <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-md'>
      <DialogHeader>
        <DialogTitle>{NKA_DISMISS_IMPORTED_TITLE}</DialogTitle>
      </DialogHeader>
      {state === 'coded-blocked' ? (
        <>
          <p className='text-sm' data-testid='nka-coded-blocked'>
            {NKA_CODED_ALLERGIES_BLOCK_COPY}
          </p>
          <DialogFooter>
            <Button variant='outline' onClick={onClose}>
              Close
            </Button>
          </DialogFooter>
        </>
      ) : (
        <>
          <p className='text-sm' data-testid='nka-dismiss-warning'>
            {NKA_DISMISS_IMPORTED_WARNING}
          </p>
          <ul className='list-disc pl-5 text-sm space-y-1'>
            {lines.map(line => (
              <li key={line.id} data-testid='nka-dismiss-line'>
                <span className='font-medium'>
                  {line.allergen || 'Unnamed allergen'}
                </span>
                {line.reaction ? ` — ${line.reaction}` : ''}
              </li>
            ))}
          </ul>
          <DialogFooter className='gap-2'>
            <Button variant='outline' onClick={onClose} disabled={isWorking}>
              Cancel
            </Button>
            <Button
              onClick={onConfirmDismiss}
              disabled={isWorking}
              data-testid='nka-dismiss-confirm'
            >
              {isWorking && (
                <Loader2 className='h-3.5 w-3.5 mr-1.5 animate-spin' />
              )}
              {NKA_DISMISS_IMPORTED_CONFIRM_LABEL}
            </Button>
          </DialogFooter>
        </>
      )}
    </DialogContent>
  </Dialog>
);

export default NkaGuardDialog;
